import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiService } from "@/services/api/ApiService";
import { ConversationMediaItem } from "@/types/conversation/media";

interface UseConversationMediasParams {
  conversationId?: string | null;
  enabled?: boolean;
  seedMedias?: ConversationMediaItem[];
  ownerConversationId?: string | null;
}

export const useConversationMedias = (params: UseConversationMediasParams = {}) => {
  const { conversationId = null, ownerConversationId = null, enabled = true, seedMedias } = params;
  const queryClient = useQueryClient();
  const queryKey = ["conversation-medias", conversationId];

  const loadConversationMedias = useCallback(async (activeConversationId: string) => {
    const conversation = await ApiService.invoke<{ medias?: ConversationMediaItem[] }>({
      resource: "conversations",
      action: "get-by-elevenlabs-id",
      data: { elevenlabsId: activeConversationId },
    });
    return (conversation?.medias || []).filter((media) => media.type === "image");
  }, []);

  const {
    data: medias = [],
    isLoading,
    error,
    refetch,
  } = useQuery<ConversationMediaItem[]>({
    queryKey,
    queryFn: async () => {
      if (!conversationId) return [];
      return loadConversationMedias(conversationId);
    },
    enabled: enabled && !!conversationId,
    initialData: seedMedias?.length ? seedMedias : undefined,
    staleTime: 30_000,
    refetchOnMount: "always",
  });

  const fetchByConversationId = useCallback(async (nextConversationId?: string | null) => {
    const activeConversationId = nextConversationId ?? conversationId;
    if (!activeConversationId) {
      return [];
    }

    if (activeConversationId === conversationId) {
      const { data } = await refetch();
      return data || [];
    }

    return queryClient.fetchQuery({
      queryKey: ["conversation-medias", activeConversationId],
      queryFn: () => loadConversationMedias(activeConversationId),
      staleTime: 30_000,
    });
  }, [conversationId, loadConversationMedias, queryClient, refetch]);

  const deleteMedia = useCallback(async (id: string) => {
    try {
      if (!ownerConversationId || !medias.some((media) => media.id === id)) return false;
      await ApiService.invoke({
        resource: "conversations",
        action: "delete-media",
        id: ownerConversationId,
        data: { mediaId: id },
      });

      queryClient.setQueryData<ConversationMediaItem[]>(
        queryKey,
        (prev = []) => prev.filter((media) => media.id !== id),
      );
      return true;
    } catch (unknownError) {
      console.error("[useConversationMedias] Unexpected delete error:", unknownError);
      return false;
    }
  }, [medias, ownerConversationId, queryClient, queryKey]);

  const deleteAllConversationMedias = useCallback(async () => {
    if (medias.length === 0) return true;

    try {
      if (!ownerConversationId) return false;
      await ApiService.invoke({
        resource: "conversations",
        action: "delete-media",
        id: ownerConversationId,
        data: {},
      });

      queryClient.setQueryData<ConversationMediaItem[]>(queryKey, []);
      return true;
    } catch (unknownError) {
      console.error("[useConversationMedias] Unexpected bulk delete error:", unknownError);
      return false;
    }
  }, [medias, ownerConversationId, queryClient, queryKey]);

  return {
    medias,
    isLoading,
    error: error ? "Failed to fetch medias" : null,
    fetchByConversationId,
    deleteMedia,
    deleteAllConversationMedias,
  };
};

